import { FlushProfile } from './flushTypes';

// Module-level store for the flush being scrubbed on the Recipes tab, read by the stope-selected
// scene to overlay the HGL chart. Like the other scene stores it outlives component mounts, so the
// scrub control clears its own entry on unmount and prepareSceneState resets it on scene changes.
export interface FlushSceneState {
  // Which scrub control owns the overlay: a flush recipe id, or the new-flush form's key.
  activeFlushKey: string | null;
  // Cumulative water pumped, m3 - the scrub position.
  waterVolume: number;
  profile: FlushProfile | null;
  airStartDistance: number | undefined;
  // Where the flush pump takes over from the paste pump; undefined when it drives from the start.
  handoverDistance: number | undefined;
  version: number;
}

const initialState = (): FlushSceneState => ({
  activeFlushKey: null,
  waterVolume: 0,
  profile: null,
  airStartDistance: undefined,
  handoverDistance: undefined,
  version: 0,
});

let sceneState: FlushSceneState = initialState();

const stateListeners: Set<() => void> = new Set();

const notify = () => stateListeners.forEach((listener) => listener());

export const getFlushSceneState = () => sceneState;

export const updateFlushSceneState = (updates: Partial<Omit<FlushSceneState, 'version'>>) => {
  sceneState = { ...sceneState, ...updates, version: sceneState.version + 1 };
  notify();
};

// Takes the overlay down, but only while `flushKey` still owns it: an unmounting control must not
// wipe a sibling that has taken over in the meantime.
export const clearFlushSceneState = (flushKey: string) => {
  if (sceneState.activeFlushKey !== flushKey) return;
  sceneState = { ...initialState(), version: sceneState.version + 1 };
  notify();
};

export const subscribeToFlushSceneState = (listener: () => void): (() => void) => {
  stateListeners.add(listener);
  return () => { stateListeners.delete(listener); };
};

export const resetFlushSceneState = () => {
  sceneState = initialState();
  notify();
};
